import { PlayerPageContext } from '../contexts/PlayerPageContext.tsx';
import {
    decrementActualEpgIndex,
    getChannelsAsync,
    incrementActualEpgIndex,
    playerUIActions,
} from '../components/player/playerUISlice.ts';
import { useAppDispatch, useAppSelector } from '../stores/store.ts';
import { ReactNode, useEffect, useState } from 'react';
import { changeVideoAsync, playerActions } from '../components/player/playerSlice.ts';
import screenfull from 'screenfull';
import api from '../newApi/apiNet.ts';
import axios, { AxiosError } from 'axios';
import { IAxiosErrorResponse } from '../interfaces/IAxiosErrorResponse.ts';
import { useModal } from '../hooks/useModal.tsx';

interface IProps {
    children: ReactNode;
}

let lastActivity = new Date().getTime();
const uiDisplayDuration = 1000; // time in ms
const PlayerPageProvider = ({ children }: IProps) => {
    const dispatch = useAppDispatch();

    const { isOpen: isModalOpen } = useModal();
    const isMenuUIVisible = useAppSelector((state) => state.playerUI.isMenuUIVisible);
    const isMenuUIHovered = useAppSelector((state) => state.playerUI.isMenuUIHovered);
    const playerErrorStatus = useAppSelector((state) => state.player.errorStatus);
    const playerUrl = useAppSelector((state) => state.player.url);
    const playerPlaying = useAppSelector((state) => state.player.playing);
    const maxPingVideoSessionCount = 3;

    let touchStartYCord = 0;
    let touchStartXCord = 0;

    const [hlsQualityLevels, setHlsQualityLevels] = useState<{ height: number }[]>([]);
    const [isEpgDetailsModalOpen, setIsEpgDetailsModalOpen] = useState(false);
    const [pingVideoSessionCount, setPingVideoSessionCount] = useState(0);

    const setMenuVisible = () => {
        dispatch(playerUIActions.setIsMenuVisible(true));
        lastActivity = new Date().getTime();
    };

    const handleClickNextEpgItem = () => {
        setMenuVisible();
        dispatch(incrementActualEpgIndex());
    };

    const handleClickPreviousEpgItem = () => {
        setMenuVisible();
        dispatch(decrementActualEpgIndex());
    };

    const toggleFullscreen = async () => {
        const playerElem = document.querySelector('#player123');
        await screenfull.toggle(playerElem ? playerElem : undefined);
        dispatch(playerUIActions.setIsFullscreen(screenfull.isFullscreen));
    };

    const preventScroll = (e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
    };

    const handleKeyUp = async (e: KeyboardEvent) => {
        if (e.key === 'f' || e.key === 'F') await toggleFullscreen();
        if (e.key === 'ArrowUp') {
            dispatch(playerUIActions.decrementChannelToChangeIndex());
        }
        if (e.key === 'ArrowDown') {
            dispatch(playerUIActions.incrementChannelToChangeIndex());
        }
        if (e.key === 'ArrowLeft') {
            dispatch(decrementActualEpgIndex());
        }
        if (e.key === 'ArrowRight') {
            dispatch(incrementActualEpgIndex());
        }
        if (e.key === ' ') {
            dispatch(playerActions.togglePlaying());
        }
        if (e.key === 'Enter') {
            dispatch(changeVideoAsync({}));
        }
        document.getElementById('playerUITopBarMarkerBtn')?.focus();
        setMenuVisible();
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement> | TouchEvent) => {
        // e.preventDefault();
        switch (e.type) {
            case 'touchstart': {
                touchStartYCord = e.changedTouches[0].clientY;
                touchStartXCord = e.changedTouches[0].clientX;
                return;
            }
            case 'touchend': {
                if (
                    touchStartYCord > e.changedTouches[0].clientY &&
                    touchStartYCord - e.changedTouches[0].clientY > 50
                ) {
                    dispatch(playerUIActions.setActualEpgIndex(0));
                    dispatch(playerUIActions.incrementChannelToChangeIndex());
                } else if (
                    touchStartYCord < e.changedTouches[0].clientY &&
                    touchStartYCord - e.changedTouches[0].clientY < -50
                ) {
                    dispatch(playerUIActions.setActualEpgIndex(0));
                    dispatch(playerUIActions.decrementChannelToChangeIndex());
                } else if (
                    touchStartXCord < e.changedTouches[0].clientX &&
                    touchStartXCord - e.changedTouches[0].clientX < -50
                ) {
                    dispatch(playerUIActions.decrementActualEpgIndex());
                } else if (
                    touchStartXCord > e.changedTouches[0].clientX &&
                    touchStartXCord - e.changedTouches[0].clientX > 50
                ) {
                    dispatch(playerUIActions.incrementActualEpgIndex());
                }
            }
        }
        setMenuVisible();
    };

    useEffect(() => {
        if (screenfull.isEnabled) {
            const handleFullscreenChange = () => {
                dispatch(playerUIActions.setIsFullscreen(screenfull.isFullscreen));
            };
            screenfull.on('change', handleFullscreenChange);
            return () => {
                screenfull.off('change', handleFullscreenChange);
            };
        }
    }, [dispatch]);

    useEffect(() => {
        if (!isModalOpen) {
            dispatch(getChannelsAsync());
            document.getElementById('scrollChannel')?.addEventListener('touchstart', handleTouchMove);
            document.getElementById('scrollChannel')?.addEventListener('touchend', handleTouchMove);
            document.getElementById('scrollChannel')?.addEventListener('wheel', preventScroll, { passive: false });
            document.addEventListener('mousemove', setMenuVisible);
            document.addEventListener('keyup', handleKeyUp);
        }
        return () => {
            document.getElementById('scrollChannel')?.removeEventListener('touchstart', handleTouchMove);
            document.getElementById('scrollChannel')?.removeEventListener('touchend', handleTouchMove);
            document.getElementById('scrollChannel')?.removeEventListener('wheel', preventScroll);
            document.removeEventListener('mousemove', setMenuVisible);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [isModalOpen, dispatch]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isMenuUIVisible)
            interval = setInterval(() => {
                if (new Date().getTime() - lastActivity > uiDisplayDuration && isMenuUIVisible && !isModalOpen && !isMenuUIHovered) {
                    dispatch(playerUIActions.setIsMenuVisible(false));
                }
            }, 1000);
        return () => {
            clearInterval(interval);
        };
    }, [isMenuUIVisible, dispatch, isModalOpen, isMenuUIHovered]);

    useEffect(() => {
        if (!playerUrl || playerErrorStatus) return;

        let intervalId: number = 0;

        const pingVideoSession = async () => {
            try {
                await api.pingVideoSession();
                setPingVideoSessionCount(0);
            } catch (e) {
                const error = e as AxiosError<IAxiosErrorResponse>;
                if (axios.isAxiosError(error) && error.response) {
                    if (error.response.data.MsgCode === 'INVALID_VIDEO_SESSION' || pingVideoSessionCount >= maxPingVideoSessionCount) {
                        dispatch(playerActions.setErrorStatus({ title: 'Sesja wideo wygasła. Uruchom program ponownie.' }));
                        dispatch(playerActions.setUrl(''));
                        dispatch(playerActions.setPlaying(false));
                        clearInterval(intervalId);
                    }
                }
                setPingVideoSessionCount(prevCount => prevCount + 1);
            }
        };
        if (import.meta.env.VITE_BACKEND_TYPE === 'php')
            intervalId = window.setInterval(pingVideoSession, 20000);

        return () => clearInterval(intervalId);
    }, [playerUrl, playerErrorStatus, playerPlaying, pingVideoSessionCount, dispatch]);

    return <PlayerPageContext.Provider
        value={{
            hlsQualityLevels,
            isEpgDetailsModalOpen,
            handleClickPreviousEpgItem,
            handleClickNextEpgItem,
            setHlsQualityLevels,
            setIsEpgDetailsModalOpen,
            setMenuVisible,
            handleTouchMove,
            toggleFullscreen,
        }}>
        {children}
    </PlayerPageContext.Provider>;
};

export default PlayerPageProvider;